'use client';

import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';

import { useRouter } from '@/i18n/routing';

import type { OcrErrorCode, OcrJobLifecyclePayload } from '../types';

const ERROR_KEY: Record<OcrErrorCode, string> = {
  no_images: 'errors.no_images',
  template_missing: 'errors.template_missing',
  ai_quota: 'errors.ai_quota',
  ai_timeout: 'errors.ai_timeout',
  ai_safety: 'errors.ai_safety',
  ai_refused: 'errors.ai_refused',
  client_model_auth: 'errors.client_model_auth',
  client_model_quota: 'errors.client_model_quota',
  client_model_timeout: 'errors.client_model_timeout',
  client_model_unreachable: 'errors.client_model_unreachable',
  client_model_bad_response: 'errors.client_model_bad_response',
  client_model_redirect_refused: 'errors.client_model_redirect_refused',
  client_model_blocked_destination: 'errors.client_model_blocked_destination',
  client_model_bad_config: 'errors.client_model_bad_config',
  parse_failed: 'errors.parse_failed',
  superseded: 'errors.superseded',
  unknown: 'errors.unknown'
};

/**
 * Centralised toast factory for OCR lifecycle events. Lets every entry
 * point (start button, user-channel hook) emit identical, actionable
 * toasts with deep-links to the relevant container.
 */
export function useOcrToast() {
  const t = useTranslations('ocr');
  const router = useRouter();

  const started = useCallback(
    (containerId: number, containerLabel?: string) => {
      toast.success(
        containerLabel
          ? t('startedWithLink', { container: containerLabel })
          : t('startSuccess'),
        {
          action: {
            label: t('viewProgress'),
            onClick: () =>
              router.push(`/dashboard/ocr-containers/${containerId}`)
          }
        }
      );
    },
    [t, router]
  );

  const completed = useCallback(
    (event: OcrJobLifecyclePayload) => {
      toast.success(
        event.container_label
          ? t('completedWithLink', { container: event.container_label })
          : t('completedShort'),
        {
          action: event.container_id
            ? {
                label: t('viewResults'),
                onClick: () =>
                  router.push(`/dashboard/ocr-containers/${event.container_id}`)
              }
            : undefined
        }
      );
    },
    [t, router]
  );

  const failed = useCallback(
    (event: OcrJobLifecyclePayload) => {
      const errorKey: OcrErrorCode = event.error_code ?? 'unknown';
      toast.error(
        event.container_label
          ? t('failedWithLink', { container: event.container_label })
          : t('failedShort'),
        {
          description: t(ERROR_KEY[errorKey]),
          action: event.container_id
            ? {
                label: t('viewResults'),
                onClick: () =>
                  router.push(`/dashboard/ocr-containers/${event.container_id}`)
              }
            : undefined
        }
      );
    },
    [t, router]
  );

  return { started, completed, failed };
}
